/**
 * GlobalModals Component
 *
 * Renders global modals based on store state.
 * Includes QuickStatsModal, AnalysisConfigPanel, GameModal, and chart modals.
 */

import { useAuth } from '@contexts/AuthContext';
import { useToast } from '@contexts/ToastContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { ApiError } from '@parlay-picker/shared/api/httpClient';
import type { GatedFeature } from '@parlay-picker/shared/featureAccess';
import {
  describeConfigAdjustments,
  TIER_LIMIT_ERROR_CODE,
  upgradeFeatureFromBody,
} from '@parlay-picker/shared/tierInputPolicy';
import { useAnalysisConfigStore } from '@stores/analysisConfigStore';
import { useGlobalShortcutsStore } from '@stores/globalShortcutsStore';
import { type ReactElement, useState } from 'react';
import AnalysisConfigPanel from './AnalysisConfigPanel';
import ErrorModal from './ErrorModal';
import { GameModal } from './GameModal';
import GameStatsChartModal from './GameStatsChartModal';
import QuickStatsModal from './QuickStatsModal';
import StatsChartModal from './StatsChartModal';
import UpgradeModal from './UpgradeModal';

export function GlobalModals(): ReactElement {
  const { authFetch } = useAuth();

  // Global shortcuts store
  const isQuickStatsOpen = useGlobalShortcutsStore((s) => s.isQuickStatsOpen);
  const isConfigPanelOpen = useGlobalShortcutsStore((s) => s.isConfigPanelOpen);
  const chartModal = useGlobalShortcutsStore((s) => s.chartModal);
  const gameModal = useGlobalShortcutsStore((s) => s.gameModal);
  const { closeQuickStats, closeConfigPanel, setChartModal, closeGameModal } =
    useGlobalShortcutsStore();

  const { showToast } = useToast();
  const { tier } = useFeatureAccess();

  // Analysis config store
  const config = useAnalysisConfigStore((s) => s.config);
  const settledLegCount = useAnalysisConfigStore((s) => s.settledLegCount);
  const { setConfig, saveConfig, hasUnsavedChanges } = useAnalysisConfigStore();

  // Handle save config with authFetch
  const [saveError, setSaveError] = useState<{ type: string; message: string } | null>(null);
  const [saveUpgradeFeature, setSaveUpgradeFeature] = useState<GatedFeature | null>(null);
  const [upgradeOpenFor, setUpgradeOpenFor] = useState<GatedFeature | null>(null);

  const handleSaveConfig = async (): Promise<boolean> => {
    try {
      const adjusted = await saveConfig(authFetch);
      const message = describeConfigAdjustments(adjusted, tier);
      if (message) showToast(`Saved. ${message}`, 'info');
      return true;
    } catch (err) {
      const isTierLimit =
        err instanceof ApiError && err.responseData?.code === TIER_LIMIT_ERROR_CODE;
      setSaveError({
        type: isTierLimit ? 'tier_limit' : 'generic',
        message: ApiError.getUserMessage(err, 'Failed to save config'),
      });
      setSaveUpgradeFeature(
        isTierLimit && err instanceof ApiError ? upgradeFeatureFromBody(err.responseData) : null,
      );
      return false;
    }
  };

  return (
    <>
      {/* Quick Stats Lookup Modal */}
      <QuickStatsModal
        isOpen={isQuickStatsOpen}
        onClose={closeQuickStats}
        onPlayerSelect={(data, leagueKey) => {
          // Don't close QuickStatsModal - let user search again
          setChartModal({
            leagueKey,
            isGameProp: false,
            playerName: data.playerName,
            prop: data.opponentAbbr
              ? `${data.playerName} vs ${data.opponentAbbr}`
              : `${data.playerName} Stats`,
            recentGames: data.recentGames,
            playerTeamName: data.team ?? undefined,
            h2hGames: data.h2hGames,
            opponentAbbr: data.opponentAbbr,
          });
        }}
        onTeamSelect={(data, propType, leagueKey) => {
          // Don't close QuickStatsModal - let user search again
          setChartModal({
            leagueKey,
            isGameProp: true,
            teamName: data.teamName,
            prop: `${data.teamName} (${data.record})`,
            teamRecentGames: data.recentGames,
            propType,
          });
        }}
        onTeamH2HSelect={(data, propType, leagueKey) => {
          // Team H2H comparison - show both teams' stats and H2H history
          setChartModal({
            leagueKey,
            isGameProp: true,
            teamName: data.team1Stats.teamName,
            prop: `${data.team1Stats.teamName} vs ${data.team2Stats.teamName} H2H`,
            teamRecentGames: data.team1Stats.recentGames,
            teamH2hGames: data.h2hHistory,
            propType,
            opponentTeamName: data.team2Stats.teamName,
            opponentTeamRecentGames: data.team2Stats.recentGames,
          });
        }}
      />

      {/* Analysis Config Panel */}
      <AnalysisConfigPanel
        isOpen={isConfigPanelOpen}
        onClose={closeConfigPanel}
        config={config}
        setConfig={setConfig}
        onSave={handleSaveConfig}
        hasUnsavedChanges={hasUnsavedChanges()}
        settledLegCount={settledLegCount}
      />
      <ErrorModal
        error={saveError}
        onDismiss={() => setSaveError(null)}
        onUpgrade={saveUpgradeFeature ? () => setUpgradeOpenFor(saveUpgradeFeature) : undefined}
      />
      {upgradeOpenFor && (
        <UpgradeModal
          mode="feature"
          feature={upgradeOpenFor}
          onDismiss={() => setUpgradeOpenFor(null)}
        />
      )}

      {/* Chart Modals - render based on chartModal state */}
      {chartModal?.isGameProp && (
        <GameStatsChartModal
          key={`${chartModal.teamName}-${chartModal.propLine}-${chartModal.propType}`}
          leagueKey={chartModal.leagueKey}
          onClose={() => setChartModal(null)}
          teamName={chartModal.teamName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.teamRecentGames ?? []}
          h2hGames={chartModal.teamH2hGames}
          propType={chartModal.propType ?? 'spread'}
          propLine={chartModal.propLine ?? undefined}
          opponentTeamName={chartModal.opponentTeamName}
          opponentTeamRecentGames={chartModal.opponentTeamRecentGames}
          marketKey={chartModal.marketKey}
        />
      )}
      {chartModal && !chartModal.isGameProp && (
        <StatsChartModal
          key={`${chartModal.playerName}-${chartModal.propLine}-${chartModal.statKey}`}
          leagueKey={chartModal.leagueKey}
          onClose={() => setChartModal(null)}
          playerName={chartModal.playerName ?? ''}
          prop={chartModal.prop ?? ''}
          recentGames={chartModal.recentGames}
          h2hGames={chartModal.h2hGames}
          propLine={chartModal.propLine ?? undefined}
          statKey={chartModal.statKey ?? undefined}
          opponentAbbr={chartModal.opponentAbbr}
          overUnder={chartModal.overUnder}
          teamName={chartModal.playerTeamName}
          marketKey={chartModal.marketKey}
        />
      )}

      {/* Unified Game Modal (schedule + box score + lineup) */}
      {gameModal && <GameModal state={gameModal} onClose={closeGameModal} />}
    </>
  );
}
